import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, TextField, MenuItem, Button, Stack, CircularProgress, Snackbar, Alert, InputAdornment, Typography,
} from '@mui/material';
import { useSalonContextStore } from '../../store/salonContextStore';
import { attendanceApi } from '../../services/employeeService';
import { PageHeader, SectionCard, FormSection, FieldGrid, LoadingState } from '../../components/ui';
import { tokens } from '../../theme';

interface Settings {
  settlementStepMinutes: number;
  workTimeRoundingStrategy: string;
  breakTimeRoundingStrategy: string;
  minimumBreakSettlementMinutes: number;
}

const STRATEGIES = [
  { v: 'FLOOR', l: 'W dół' }, { v: 'CEIL', l: 'W górę' }, { v: 'NEAREST', l: 'Do najbliższego' }, { v: 'NONE', l: 'Bez zaokrąglania' },
];

const minutesAdornment = { endAdornment: <InputAdornment position="end">min</InputAdornment> };

export const SettlementSettingsPage: React.FC = () => {
  const { activeSalonId } = useSalonContextStore();
  const [s, setS] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);

  const load = useCallback(async () => {
    if (!activeSalonId) return;
    setLoading(true);
    try { setS(await attendanceApi.getSettings(activeSalonId) as Settings); } finally { setLoading(false); }
  }, [activeSalonId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!activeSalonId || !s) return;
    setSaving(true);
    try {
      await attendanceApi.updateSettings(activeSalonId, {
        settlementStepMinutes: Number(s.settlementStepMinutes),
        workTimeRoundingStrategy: s.workTimeRoundingStrategy,
        breakTimeRoundingStrategy: s.breakTimeRoundingStrategy,
        minimumBreakSettlementMinutes: Number(s.minimumBreakSettlementMinutes),
      });
      setToast({ msg: 'Zapisano ustawienia', sev: 'success' });
    } catch {
      setToast({ msg: 'Nie udało się zapisać ustawień. Sprawdź wartości i spróbuj ponownie.', sev: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const header = (
    <PageHeader
      breadcrumbs={[{ label: 'Wynagrodzenia' }]}
      title="Ustawienia rozliczania czasu"
      description="Czas faktyczny nigdy nie jest nadpisywany. Te reguły zmieniają wyłącznie czas rozliczeniowy, od którego liczone są wynagrodzenia."
    />
  );

  if (loading || !s) {
    return (
      <Box sx={{ maxWidth: 920 }}>
        {header}
        <LoadingState variant="block" height={320} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 920 }}>
      {header}
      <SectionCard>
        <FormSection
          first
          sx={{ pt: { xs: 0, md: 0 } }}
          title="Czas pracy"
          description="Jak liczyć godziny od wejścia do wyjścia pracownika."
        >
          <FieldGrid columns={2}>
            <TextField
              label="Blok rozliczeniowy"
              type="number"
              value={s.settlementStepMinutes}
              onChange={(e) => setS({ ...s, settlementStepMinutes: Number(e.target.value) })}
              InputProps={minutesAdornment}
              inputProps={{ min: 0 }}
              helperText="Czas liczony pełnymi blokami. Domyślnie 30 min."
              fullWidth
            />
            <TextField
              select
              label="Zaokrąglanie czasu pracy"
              value={s.workTimeRoundingStrategy}
              onChange={(e) => setS({ ...s, workTimeRoundingStrategy: e.target.value })}
              helperText="Domyślnie w dół, np. 7 h 45 min → 7 h 30 min."
              fullWidth
            >
              {STRATEGIES.map((o) => <MenuItem key={o.v} value={o.v}>{o.l}</MenuItem>)}
            </TextField>
          </FieldGrid>
        </FormSection>

        <FormSection
          title="Wyjścia i przerwy"
          description="Jak liczyć czas, gdy pracownik wychodzi w trakcie zmiany."
        >
          <FieldGrid columns={2}>
            <TextField
              select
              label="Zaokrąglanie wyjść"
              value={s.breakTimeRoundingStrategy}
              onChange={(e) => setS({ ...s, breakTimeRoundingStrategy: e.target.value })}
              helperText="Domyślnie w górę."
              fullWidth
            >
              {STRATEGIES.map((o) => <MenuItem key={o.v} value={o.v}>{o.l}</MenuItem>)}
            </TextField>
            <TextField
              label="Minimalny blok wyjścia"
              type="number"
              value={s.minimumBreakSettlementMinutes}
              onChange={(e) => setS({ ...s, minimumBreakSettlementMinutes: Number(e.target.value) })}
              InputProps={minutesAdornment}
              inputProps={{ min: 0 }}
              helperText="Np. przy 30 min wyjście na 15 min liczy się jako 30 min."
              fullWidth
            />
          </FieldGrid>
        </FormSection>

        <Stack
          direction={{ xs: 'column-reverse', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          gap={1.5}
          sx={{ borderTop: `1px solid ${tokens.line}`, pt: 2.5 }}
        >
          <Typography variant="body2" sx={{ color: tokens.muted }}>
            Nowe reguły dotyczą zmian rozliczanych po zapisie — wcześniejsze nie są przeliczane.
          </Typography>
          <Button
            variant="contained"
            onClick={save}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {saving ? 'Zapisywanie…' : 'Zapisz ustawienia'}
          </Button>
        </Stack>
      </SectionCard>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert severity={toast.sev} variant="filled" onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};

export default SettlementSettingsPage;
